import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {AuthApi, PortalApi} from "components/src/api/portal/generated";
import {TfaSetupWizard} from "components/src/auth/TfaSetupWizard";
import {DialogCloseCause} from "components/src/common/Enums";
import {Loading} from "components/src/common/Loading";
import React, {useEffect, useState} from "react";

interface TfaSetupRequiredProps {
    children: React.ReactNode;
}

/**
 * Wraps authenticated app content. When portal-2fa-required is enabled and the current user
 * has not yet configured 2FA, the 2FA setup wizard is shown instead of the app. The wizard
 * cannot be dismissed until setup is complete.
 */
export const TfaSetupRequired = ({children}: TfaSetupRequiredProps) =>
{
    const [loading, setLoading] = useState(true);
    const [tfaRequired, setTfaRequired] = useState(false);
    const [tfaEnabled, setTfaEnabled] = useState(false);

    const authApi = new AuthApi(ApiConfig.config());
    const portalApi = new PortalApi(ApiConfig.config());

    const fetchPortalProperties = () => {
        setLoading(true);
        portalApi.portalControllerGetPortalProperties()
            .then(response => {
                setTfaRequired(response.data.tfaRequired ?? false);
                setTfaEnabled(response.data.tfaEnabled ?? false);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchPortalProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return <Loading/>;
    }

    if (tfaRequired && !tfaEnabled) {
        return (
            <TfaSetupWizard
                is2FAEnabledFunc={() =>
                    portalApi.portalControllerGetPortalProperties().then(r => ({
                        ...r,
                        data: r.data.tfaEnabled ?? false,
                    }))
                }
                generateSecretFunc={() => authApi.authControllerGenerateTFASecret()}
                createSecretQRCodeFunc={(secret: string) => authApi.authControllerCreate2FASecretQRCode(secret)}
                set2FAEnabledFunc={(enabled: boolean) => authApi.authControllerSet2FAEnabled(enabled)}
                validateOTPFunc={(otpCode: string, secret: string) => authApi.authControllerValidateTOTP({otp : otpCode, tfaSecret : secret})}
                setSecretFunc={(secret: string) => authApi.authControllerSet2FASecret(secret)}
                tfaRequired={true}
                onClose={(closeCause: DialogCloseCause) => {
                    if (closeCause === DialogCloseCause.Succes) {
                        fetchPortalProperties();
                    }
                    // On Cancel: do not update state — wizard stays mounted because
                    // tfaRequired && !tfaEnabled is still true.
                }}
            />
        );
    }

    return <>{children}</>;
};
